import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { functionsUrl, supabase } from '@radiology/api-client';

/**
 * The shared administration surface.
 *
 * One implementation for all four modules, replacing three partial copies (the
 * certifications, purchasing and devices apps each had their own user management
 * and settings screens, backed by Keycloak admin calls made with the realm
 * administrator's own credentials).
 *
 * Almost everything here goes straight to PostgREST with the caller's own JWT,
 * so the per-module admin boundary is enforced by RLS rather than by this file.
 * The three operations that genuinely need the service key — creating an
 * account, disabling one, forcing a reset — go through the `admin-users` Edge
 * Function, which re-checks that boundary because the service key bypasses RLS.
 */

export type ModuleKey = 'certifications' | 'purchasing' | 'attendance' | 'devices';

export interface AdminUser {
  user_id: string;
  username: string;
  display_name: string | null;
  email: string | null;
  hospital: string | null;
  section: string | null;
  title: string | null;
  badge_number: string | null;
  phone: string | null;
  department_id: string | null;
  must_reset_password: boolean;
  disabled: boolean;
  last_sign_in_at: string | null;
  created_at: string;
  roles: Partial<Record<ModuleKey, string[]>>;
}

const USERS = ['admin', 'users'] as const;

/**
 * `core.list_users()` rather than a table read: the email address and last
 * sign-in live in `auth.users`, which `authenticated` has no rights on — that
 * table also holds password hashes and recovery tokens. The function is
 * SECURITY DEFINER with an explicit administrator guard.
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: USERS,
    queryFn: async (): Promise<AdminUser[]> => {
      // supabase-js types an untyped rpc() as a single row, and there are no
      // generated database types here, so the result is narrowed by hand.
      const result = (await supabase().rpc('list_users')) as unknown as {
        data: AdminUser[] | null;
        error: { message: string } | null;
      };
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
  });
}

/** Which modules the signed-in admin may actually administer. */
export function useAdminModules() {
  return useQuery({
    queryKey: ['admin', 'modules'],
    queryFn: async (): Promise<ModuleKey[]> => {
      const result = (await supabase().rpc('admin_modules')) as unknown as {
        data: ModuleKey[] | null;
        error: { message: string } | null;
      };
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Correct a profile.
 *
 * This is the screen that fixes owner actions O9 and O11 — the purchasing
 * manager sitting at hospital `test H`, and three devices secretaries with no
 * hospital at all. `username` is deliberately not editable: it is the join key
 * to every migrated row in all four modules.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      userId: string;
      display_name?: string | null;
      hospital?: string | null;
      section?: string | null;
      title?: string | null;
      badge_number?: string | null;
      phone?: string | null;
      department_id?: string | null;
    }) => {
      const { userId, ...patch } = input;
      const { error } = await supabase().from('user_profiles').update(patch).eq('user_id', userId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS }),
  });
}

/**
 * Grant or revoke one role.
 *
 * No client-side check that the module is one the caller administers — the
 * policy is the check. Attempting a module you do not administer raises 42501 on
 * insert, and silently affects zero rows on delete.
 */
export function useSetRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      userId: string;
      module: ModuleKey;
      role: string;
      grant: boolean;
    }) => {
      const table = supabase().from('user_roles');
      const { error } = input.grant
        ? await table.insert({ user_id: input.userId, module: input.module, role: input.role })
        : await table
            .delete()
            .eq('user_id', input.userId)
            .eq('module', input.module)
            .eq('role', input.role);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS }),
  });
}

/** Calls into the admin-users Edge Function with the caller's own token. */
async function adminCall(action: string, body: unknown): Promise<void> {
  const { data } = await supabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session has expired. Sign in again.');

  const res = await fetch(functionsUrl(`admin-users/${action}`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: String(import.meta.env.VITE_SUPABASE_ANON_KEY),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(payload.detail ?? payload.error ?? 'That did not work.');
  }
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      username: string;
      email: string;
      displayName?: string;
      hospital?: string;
      section?: string;
      title?: string;
      badgeNumber?: string;
      phone?: string;
      department?: string;
      roles: { module: ModuleKey; role: string }[];
    }) => adminCall('create', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS }),
  });
}

export function useSetDisabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; disabled: boolean }) => adminCall('disable', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS }),
  });
}

/**
 * Force a password reset.
 *
 * Sets the flag only. The administrator does not get to choose a password —
 * they would then know it. The user goes through `auth-reset` like everybody
 * else, proving control of their mailbox.
 */
export function useForceReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminCall('reset', { userId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS }),
  });
}

// -----------------------------------------------------------------------------
// System settings — core.attributes
// -----------------------------------------------------------------------------

export interface Attribute {
  id: string;
  module: ModuleKey;
  key: string;
  name: string;
  created_at: string;
}

const ATTRS = ['admin', 'attributes'] as const;

export function useAttributes(module: ModuleKey) {
  return useQuery({
    queryKey: [...ATTRS, module],
    queryFn: async (): Promise<Attribute[]> => {
      const { data, error } = await supabase()
        .from('attributes')
        .select('*')
        .eq('module', module)
        .order('key')
        .order('name')
        .returns<Attribute[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useSaveAttribute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { module: ModuleKey; key: string; name: string }) => {
      const { error } = await supabase().from('attributes').insert(input);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ATTRS }),
  });
}

export function useDeleteAttribute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('attributes').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ATTRS }),
  });
}

// -----------------------------------------------------------------------------
// Audit log
// -----------------------------------------------------------------------------

export interface AuditEntry {
  id: string;
  module: string;
  username: string;
  event: string | null;
  role_choice: string | null;
  hospital_choice: string | null;
  section_choice: string | null;
  device: string | null;
  ip: string | null;
  login_at: string | null;
  created_at: string;
}

/**
 * The log had RLS enabled with no policies at all from Phase 0 until Phase 8, so
 * it was readable by nobody. It is now scoped to the modules the caller
 * administers, plus `auth` — sign-ins and resets are cross-cutting.
 */
export function useAuditLog(filters: { module?: string; username?: string; days: number }) {
  return useQuery({
    queryKey: ['admin', 'audit', filters],
    queryFn: async (): Promise<AuditEntry[]> => {
      const since = new Date(Date.now() - filters.days * 86400000).toISOString();

      let query = supabase()
        .from('audit_log')
        .select('*')
        .gte('login_at', since)
        .order('login_at', { ascending: false })
        .limit(1000);

      if (filters.module) query = query.eq('module', filters.module);
      if (filters.username) query = query.ilike('username', `%${filters.username}%`);

      const { data, error } = await query.returns<AuditEntry[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** Role vocabularies per module, matching core.app_role and the nav. */
export const ROLES_BY_MODULE: Record<ModuleKey, string[]> = {
  certifications: ['employee', 'supervisor', 'manager', 'operation-manager', 'admin'],
  purchasing: ['employee', 'approver', 'chief-approver', 'manager', 'admin'],
  attendance: ['secretary', 'manager', 'admin'],
  devices: ['secretary', 'manager', 'admin'],
};

export const MODULE_LABELS: Record<ModuleKey, string> = {
  certifications: 'Certifications',
  purchasing: 'Purchase Tracking',
  attendance: 'QR Attendance',
  devices: 'Medical Devices',
};
